<script setup>
/**
 * The Mystic who read it: one maker's mark, the same everywhere (lesson-redo
 * DESIGN.md I; BUILD-BRIEF fixed decisions). A small square portrait on a
 * glass tile in the picture's bottom-left corner. Mystics are squares.
 *
 * - 28px seen (30 in the window), 44px to hit (an invisible halo).
 * - `side="right"` (the feed card, 2026-11-02) sits it bottom-right, clear of
 *   the sharer's chip, its label sliding out to the left.
 * - The portrait: the Mystic's own, else Mystic Aida's (HOUSE_PORTRAIT), the
 *   initial only when both fail (prod bug 2026-11-02).
 * - Hover or focus slides out "Read by {Mystic}".
 * - A mouse click (or Enter) opens the Mystic's page. On touch the first tap
 *   reveals the label (aria-expanded) and the second opens the page.
 * - Its tap is always its own: never the lesson's, never the picture's.
 *
 * Emits `open` (sub) after asking the app to open the page; the open is
 * counted for the Mystic's Studio (search log, source `lesson`).
 */
import { computed, ref, watch } from 'vue'
import { HOUSE_PORTRAIT, openMystic } from '@/utils/lesson'
import { logSearch } from '@/api-service'

const props = defineProps({
  mystic: { type: Object, required: true },   // { name, sub, avatar }
  // 'card' (the feed, 28px) or 'window' (the lesson's picture, 30px).
  size: { type: String, default: 'card' },
  // 'left' (default) or 'right' (the feed card, 2026-11-02).
  side: { type: String, default: 'left' },
})
const emit = defineEmits(['open'])

const open = ref(false)
let touch = false
// 0: its own portrait, 1: Mystic Aida's, 2: the letter.
const fell = ref(0)
watch(() => props.mystic && props.mystic.avatar, () => { fell.value = 0 })
const own = computed(() => String((props.mystic && props.mystic.avatar) || '').trim())
const portrait = computed(() => {
  if (fell.value === 0 && own.value) return own.value
  return fell.value < 2 && own.value !== HOUSE_PORTRAIT ? HOUSE_PORTRAIT : ''
})
function onImgError() { fell.value = own.value && fell.value === 0 && own.value !== HOUSE_PORTRAIT ? 1 : 2 }
const label = computed(() => `Read by ${props.mystic.name || 'Mystic Aida'}`)
const initial = computed(() => String(props.mystic.name || '✦').trim().charAt(0).toUpperCase())

function onPointer(e) { touch = !!e && (e.pointerType === 'touch' || e.pointerType === 'pen') }
function onClick(e) {
  if (e) { e.stopPropagation(); e.preventDefault() }
  if (touch && !open.value) { open.value = true; touch = false; return }
  touch = false
  if (!openMystic(props.mystic.sub, 'lesson')) return
  // Counted as an open from a lesson (API.md §2); never in the way of the tap.
  try { logSearch({ q: '', kind: 'tap', sub_domain: props.mystic.sub, source: 'lesson' }) } catch (err) { /* not counted */ }
  emit('open', props.mystic.sub)
}
</script>

<template>
  <button type="button" class="mk" :class="[size, { open, right: side === 'right' }]" data-testid="mystic-mark"
          :aria-label="label" :aria-expanded="open ? 'true' : 'false'"
          @pointerdown.stop="onPointer" @click="onClick" @keydown.enter.stop @keydown.space.stop @blur="open = false">
    <span class="sqm" aria-hidden="true">
      <img v-if="portrait" :src="portrait" alt="" @error="onImgError" />
      <span v-else class="sqm-ph">{{ initial }}</span>
    </span>
    <span class="mk-t" aria-hidden="true">{{ label }}</span>
  </button>
</template>

<style scoped>
.mk {
  position: absolute; left: 10px; bottom: 10px; z-index: 3; display: inline-flex; align-items: center; height: 34px; padding: 3px; border: 0; border-radius: 9px;
  cursor: pointer; background: rgba(20, 9, 38, .55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 0 0 1px rgba(231, 201, 131, .6); transition: background .2s; font: inherit;
}
/* 44px to hit, whatever is seen. */
.mk::after { content: ''; position: absolute; inset: -5px; }
.sqm { width: 28px; height: 28px; border-radius: 6px; overflow: hidden; flex: none; border: 1px solid rgba(231, 201, 131, .8); background: #2a1845; display: block; }
.sqm img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sqm-ph { display: grid; place-items: center; width: 100%; height: 100%; font: 600 13px/1 'Letter Display', 'Cinzel', Georgia, serif; color: #e7c983; }
.mk-t {
  max-width: 0; overflow: hidden; white-space: nowrap; font: 600 12px Inter, system-ui, sans-serif; color: #ffe9b8;
  transition: max-width .35s cubic-bezier(.2, .8, .2, 1), padding .35s;
}
.mk:hover .mk-t, .mk:focus-visible .mk-t, .mk.open .mk-t { max-width: 200px; padding: 0 8px; }
/* The feed card's corner (2026-11-02): bottom-right, the label opening leftwards. */
.mk.right { left: auto; right: 10px; flex-direction: row-reverse; }
.mk:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }
/* The window's mark: 30px, 12px in from the edge; the label reads as a link. */
.mk.window { left: 12px; bottom: 12px; height: 36px; }
.mk.window .sqm { width: 30px; height: 30px; border-color: rgba(231, 201, 131, .85); }
.mk.window .mk-t { font-size: 12.5px; }
.mk.window:hover .mk-t, .mk.window:focus-visible .mk-t, .mk.window.open .mk-t {
  padding: 0 10px 0 8px; text-decoration: underline; text-decoration-color: rgba(255, 233, 184, .45); text-underline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) { .mk-t { transition: none; } }
</style>
